import { useEffect, useMemo, useState } from 'react';
import { Languages, Loader2, Sparkles, X } from 'lucide-react';
import { toast } from 'sonner';

// Persisted dismissal flag for the create-mode onboarding card. Bumped to
// .v1 so we can invalidate (.v2 etc) if we materially rewrite the steps.
const PRODUCT_EDITOR_TOUR_DISMISS_KEY = 'asb.product-editor-tour-dismissed.v1';

import {
  ALL_BRANDS,
  ALL_INDUSTRIES,
  ALL_LANGS,
  type Brand,
  type BrandLangMap,
  type Lang,
  type LangMap,
  type ProductItem,
} from '@asb/shared';

import {
  useAdminProductsQuery,
  useTranslateProductMutation,
  useUpsertProductMutation,
} from '../../lib/queries';
import { t } from '../../lib/translations';
import ErrorBanner from '../ErrorBanner';

// ───────────────────────────────────────────
// suggestId: derive an internal short code from the English product name.
// Editors no longer see "ID" as a separate concept — they fill the name in
// 4 langs, and we extract a short code from name.en at save time. The code
// becomes products.id (PK) and is what AI prompts / golden eval / logs
// reference. This tradeoff keeps the AI prompt token-efficient and trace
// human-readable while removing the "what's an ID?" cognitive burden.
//
// Heuristics, in priority order:
//   1. Parenthesized ALL-CAPS acronym wins:
//      "Customer Relationship Management (CRM)" → "CRM"
//   2. Multi-word: initials, uppercased: "Order Management System" → "OMS"
//   3. Single word: capitalized + truncated: "Settlement" → "Settlement"
//   4. No English letters → '' (caller blocks save with a "fill EN name" hint)
//
// Two products with name.en yielding the same suggestId collide on PK and
// the save fails — callers should compute the collision against existing
// IDs and surface a "rename to differentiate" hint inline before submit.
// ───────────────────────────────────────────
export function suggestId(enName: string): string {
  if (!enName) return '';
  const trimmed = enName.trim();
  const paren = trimmed.match(/\(([A-Z]{2,8})\)/);
  if (paren?.[1]) return paren[1];
  const cleaned = trimmed.replace(/[^a-zA-Z\s]/g, ' ').replace(/\s+/g, ' ').trim();
  if (!cleaned) return '';
  const words = cleaned.split(' ').filter((w) => w.length > 0);
  if (words.length === 0) return '';
  if (words.length >= 2) {
    return words.slice(0, 6).map((w) => w.charAt(0).toUpperCase()).join('');
  }
  const w = words[0]!;
  return w.charAt(0).toUpperCase() + w.slice(1, 12).toLowerCase();
}

// ownerEmail is server-computed (left-join); Draft never carries it.
// ownerId defaults to null on create — server sets it to the current user for
// editors, or accepts an explicit value from super_admin.
type Draft = Omit<ProductItem, 'createdAt' | 'updatedAt' | 'ownerEmail'>;

function emptyLangMap(): LangMap {
  return { 'zh-CN': '', 'zh-HK': '', en: '', ja: '' };
}
function emptyBrandLangMap(): BrandLangMap {
  return { google: emptyLangMap(), aws: emptyLangMap(), huawei: emptyLangMap() };
}
function toDraft(p?: ProductItem | null): Draft {
  if (!p) {
    return {
      id: '',
      name: emptyLangMap(),
      description: emptyLangMap(),
      audience: emptyLangMap(),
      url: emptyBrandLangMap(),
      isParticipating: true,
      ownerId: null,
      industries: [],
    };
  }
  const { createdAt: _c, updatedAt: _u, ownerEmail: _oe, ...rest } = p;
  // Backfill industries=[] for legacy products created before migration 0005
  // so the editor's chip-toggle helpers don't have to handle undefined.
  return { industries: [], ...rest };
}

const LANG_LABEL: Record<Lang, string> = {
  'zh-CN': '简中',
  'zh-HK': '繁中',
  en: 'EN',
  ja: '日本語',
};
const BRAND_LABEL: Record<Brand, string> = { google: 'Google', aws: 'AWS', huawei: 'Huawei' };

interface Props {
  lang: Lang;
  /** null = create mode, ProductItem = edit mode */
  initial: ProductItem | null;
  onClose: () => void;
}

/**
 * Modal editor for create / edit. Uses a tabbed language editor so the admin
 * doesn't face 4 copies of name/description/audience inline at once.
 *
 * Identity model: the English name (name.en) is the user-facing identifier.
 * On create, the system silently derives a short code (products.id PK) from
 * name.en via suggestId. Editors never see "ID" in the UI. On edit, the
 * existing PK is locked and only the displayed names/descriptions can change.
 */
export default function ProductEditor({ lang, initial, onClose }: Props) {
  const ui = t(lang);
  const [draft, setDraft] = useState<Draft>(toDraft(initial));
  const upsert = useUpsertProductMutation();
  const translate = useTranslateProductMutation();
  const mode = initial ? 'update' : 'create';

  // Default lang tab: in CREATE mode start on EN (because name.en is required
  // to derive the internal id; landing on EN avoids the "fill EN to enable
  // save" disabled-state surprise). In EDIT mode keep the user's current UI
  // lang tab so they edit in the language they're already reading in.
  const [editingLang, setEditingLang] = useState<Lang>(initial ? lang : 'en');

  // Onboarding card visibility — only on create + only when localStorage flag
  // absent. Dismissal is persisted per-browser. super_admin sees this too:
  // first time creating a product is confusing regardless of role.
  const [tourVisible, setTourVisible] = useState<boolean>(false);
  useEffect(() => {
    if (mode !== 'create') return;
    try {
      setTourVisible(localStorage.getItem(PRODUCT_EDITOR_TOUR_DISMISS_KEY) !== '1');
    } catch {
      // private mode / storage blocked — silently keep tour hidden
    }
  }, [mode]);
  const dismissTour = () => {
    try {
      localStorage.setItem(PRODUCT_EDITOR_TOUR_DISMISS_KEY, '1');
    } catch {
      // ignore
    }
    setTourVisible(false);
  };

  // Existing IDs for collision validation. Hits the same react-query cache as
  // ProductList so no extra network request.
  const productsQuery = useAdminProductsQuery(true);
  const existingIds = useMemo(() => {
    const ids = new Set((productsQuery.data ?? []).map((p) => p.id));
    // In edit mode, the current product's own ID is allowed (locked anyway).
    if (initial) ids.delete(initial.id);
    return ids;
  }, [productsQuery.data, initial]);

  // Save-blockers in create mode:
  //   - name.en empty       → can't derive a code, server PK would be empty
  //   - derived code clashes → would 409 on insert; show inline hint pre-flight
  const enName = draft.name.en.trim();
  const derivedId = mode === 'create' ? suggestId(enName) : draft.id;
  const enNameMissing = mode === 'create' && enName === '';
  const idCollision =
    mode === 'create' && derivedId !== '' && existingIds.has(derivedId);

  // Sync draft if parent swaps the `initial` product without remounting
  useEffect(() => {
    setDraft(toDraft(initial));
  }, [initial]);

  // ───────────────────────────────
  // ✨ Translate: source = zh-CN (固定，决策 F1)，target = zh-HK / en / ja。
  // 服务端走 admin-configured LLM chain（决策 A1），D2 prompt 保留缩写，
  // E2 部分成功也写入。前端这里实现 C2 merge —— 只填 draft 里 EMPTY 的字段，
  // 不动用户已经编辑过的内容。
  // ───────────────────────────────
  const zhCnName = draft.name['zh-CN'].trim();
  const zhCnReady = zhCnName.length > 0;

  const handleTranslate = async () => {
    if (!zhCnReady) {
      toast.error(ui.adminTranslateNeedZhFirst);
      return;
    }
    try {
      const result = await translate.mutateAsync({
        name: draft.name['zh-CN'].trim(),
        description: draft.description['zh-CN'].trim(),
        audience: draft.audience['zh-CN'].trim(),
      });

      // C2 merge: 只填 draft 里当前为空的 field，保护已编辑内容
      let filledCount = 0;
      setDraft((prev) => {
        const next: Draft = {
          ...prev,
          name: { ...prev.name },
          description: { ...prev.description },
          audience: { ...prev.audience },
        };
        for (const [targetLang, fields] of Object.entries(result.translations)) {
          if (!fields) continue;
          const l = targetLang as Lang;
          for (const fkey of ['name', 'description', 'audience'] as const) {
            const current = next[fkey][l].trim();
            const incoming = fields[fkey];
            if (!current && incoming) {
              next[fkey] = { ...next[fkey], [l]: incoming };
              filledCount++;
            }
          }
        }
        return next;
      });

      if (result.failed.length > 0) {
        toast.warning(
          ui.adminTranslatePartialFailed
            .replace('{count}', String(filledCount))
            .replace('{failed}', result.failed.join(', ')),
        );
      } else if (filledCount === 0) {
        // 所有 target lang 都已经手填过了 — translate 没填进任何字段
        toast.info(ui.adminTranslateNothingToFill);
      } else {
        toast.success(ui.adminTranslateDone.replace('{count}', String(filledCount)));
      }
    } catch (e) {
      toast.error((e as Error).message || ui.adminTranslateFailed);
    }
  };

  // B3: 当前 tab 是非 zh-CN 且 3 个 field 都空 → 浮一个 inline 气泡提示
  // "从简中翻译此 tab"。点击行为跟主按钮一致（一次填全所有空 lang）。
  const currentTabEmpty =
    editingLang !== 'zh-CN' &&
    !draft.name[editingLang].trim() &&
    !draft.description[editingLang].trim() &&
    !draft.audience[editingLang].trim();

  const submit = async () => {
    if (enNameMissing || idCollision || upsert.isPending) return;
    // Stamp the derived id onto the draft right before submit so server
    // gets the canonical short code as PK.
    const product = mode === 'create' ? { ...draft, id: derivedId } : draft;
    if (!product.id.trim()) return;
    try {
      await upsert.mutateAsync({ mode, product });
      onClose();
    } catch {
      // banner
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 px-4 py-8 overflow-y-auto">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="product-editor-title"
        className="w-full max-w-2xl rounded-2xl border border-slate-200 bg-[var(--bg-surface)] p-6 my-auto"
      >
        <div className="flex items-center justify-between">
          <h2 id="product-editor-title" className="text-lg font-semibold">
            {mode === 'create' ? ui.adminAddProduct : ui.adminEditProduct}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-slate-500 hover:text-slate-900"
          >
            <X size={18} />
          </button>
        </div>

        {/* First-time onboarding card (create mode only). 4 steps cover the
            non-obvious mechanics: (1) EN-first because id derives from
            name.en; (2) tabs are independent stores; (3) industries semantics;
            (4) URL fallback rules. Dismissible via X (localStorage flag,
            sticky per browser). */}
        {tourVisible && (
          <div className="mt-5 rounded-xl border border-blue-500/20 bg-blue-500/[0.06] p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="text-xs leading-relaxed">
                <p className="text-sm font-medium text-blue-200 mb-2 inline-flex items-center gap-1.5">
                  <Sparkles size={14} className="text-blue-300" />
                  {ui.adminProductEditorTourTitle}
                </p>
                <ol className="list-decimal pl-4 space-y-1 text-slate-700">
                  <li>{ui.adminProductEditorTourStep1}</li>
                  <li>{ui.adminProductEditorTourStep2}</li>
                  <li>{ui.adminProductEditorTourStep3}</li>
                  <li>{ui.adminProductEditorTourStep4}</li>
                </ol>
              </div>
              <button
                type="button"
                onClick={dismissTour}
                aria-label={ui.adminEditorTourDismiss}
                title={ui.adminEditorTourDismiss}
                className="shrink-0 rounded-md p-1 text-slate-500 hover:bg-slate-50 hover:text-slate-900"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        )}

        {/* Industry tags — multi-select chips. Cross-cutting (not per lang),
            empty array = "applies to all industries" so the homepage filter
            never hides this product. Cards in the public catalog use these
            to power the industry filter chip row. */}
        <div className="mt-5">
          <label className="block text-xs text-slate-500 mb-1.5">{ui.adminFieldIndustries}</label>
          <div className="flex flex-wrap gap-1.5">
            {ALL_INDUSTRIES.map((ind) => {
              const selected = (draft.industries ?? []).includes(ind);
              return (
                <button
                  key={ind}
                  type="button"
                  onClick={() => {
                    const cur = draft.industries ?? [];
                    const next = selected ? cur.filter((x) => x !== ind) : [...cur, ind];
                    setDraft({ ...draft, industries: next });
                  }}
                  className={[
                    'rounded-full border px-2.5 py-1 text-[11px] transition-colors',
                    selected
                      ? 'accent-bg text-white border-transparent font-medium'
                      : 'border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100 hover:text-slate-900',
                  ].join(' ')}
                  aria-pressed={selected}
                >
                  {ui.industryLabel[ind]}
                </button>
              );
            })}
          </div>
          <p className="mt-1 text-[10px] text-slate-400 leading-relaxed">
            {(draft.industries ?? []).length === 0
              ? ui.adminFieldIndustriesEmptyHint
              : ui.adminFieldIndustriesHint}
          </p>
        </div>

        {/* Lang tabs + ✨ 一键翻译按钮 (B1)。tabs 左，翻译按钮右。
            按钮 source 固定 zh-CN (F1)，所以 zh-CN name 空时 disabled。 */}
        <div className="mt-5 flex items-center justify-between gap-3 flex-wrap">
          <div className="inline-flex rounded-full border border-slate-200 bg-slate-50 p-0.5 text-xs">
            {ALL_LANGS.map((l) => (
              <button
                key={l}
                type="button"
                onClick={() => setEditingLang(l)}
                className={[
                  'rounded-full px-3 py-1 transition-colors',
                  l === editingLang
                    ? 'accent-bg text-white font-medium'
                    : 'text-slate-600 hover:text-slate-900',
                ].join(' ')}
              >
                {LANG_LABEL[l]}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={handleTranslate}
            disabled={!zhCnReady || translate.isPending}
            data-testid="translate-button"
            title={zhCnReady ? ui.adminTranslateTitle : ui.adminTranslateNeedZhFirst}
            className="inline-flex items-center gap-1.5 rounded-full border border-blue-500/20 bg-blue-500/[0.08] px-3 py-1 text-xs font-medium text-blue-200 hover:bg-blue-500/[0.15] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {translate.isPending ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <Languages size={12} />
            )}
            {translate.isPending ? ui.adminTranslating : ui.adminTranslate}
          </button>
        </div>

        {/* B3: 当前 tab 非 zh-CN 且 3 个 field 都空 → 提示从简中翻译 */}
        {currentTabEmpty && zhCnReady && (
          <button
            type="button"
            onClick={handleTranslate}
            disabled={translate.isPending}
            data-testid="translate-bubble"
            className="mt-3 inline-flex items-center gap-2 rounded-lg border border-blue-500/20 bg-blue-500/[0.06] px-3 py-2 text-xs text-blue-200 hover:bg-blue-500/[0.1] disabled:opacity-40"
          >
            <Sparkles size={12} />
            {ui.adminTranslateTabBubble.replace('{lang}', LANG_LABEL[editingLang])}
          </button>
        )}

        {/* Name / Description / Audience — one lang at a time */}
        {(
          [
            ['name', ui.adminFieldName] as const,
            ['description', ui.adminFieldDescription] as const,
            ['audience', ui.adminFieldAudience] as const,
          ]
        ).map(([field, label]) => {
          const showNameMeta =
            field === 'name' && editingLang === 'en' && mode === 'create';
          return (
            <div key={field} className="mt-4">
              <label className="block text-xs text-slate-500">{label}</label>
              {field === 'description' ? (
                <textarea
                  rows={3}
                  value={draft[field][editingLang]}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      [field]: { ...draft[field], [editingLang]: e.target.value },
                    })
                  }
                  placeholder={ui.adminFieldDescriptionPlaceholder}
                  className="mt-1 w-full resize-y rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-[var(--accent-muted)] placeholder:text-slate-300"
                />
              ) : (
                <input
                  type="text"
                  value={draft[field][editingLang]}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      [field]: { ...draft[field], [editingLang]: e.target.value },
                    })
                  }
                  placeholder={
                    showNameMeta
                      ? 'Customer Relationship Management'
                      : field === 'audience'
                        ? ui.adminFieldAudiencePlaceholder
                        : undefined
                  }
                  className={[
                    'mt-1 w-full rounded-lg border bg-slate-50 px-3 py-2 text-sm outline-none transition-colors placeholder:text-slate-300',
                    showNameMeta && idCollision
                      ? 'border-red-500/50 focus:border-red-500/70'
                      : 'border-slate-200 focus:border-[var(--accent-muted)]',
                  ].join(' ')}
                />
              )}

              {/* Inline meta under the English name input on create:
                  - shows the auto-derived code (so editor sees what their
                    name produces under the hood, no surprise on save)
                  - flips to red collision warning if that code already exists */}
              {showNameMeta && (
                <p
                  className={[
                    'mt-1 text-[11px] leading-relaxed',
                    idCollision ? 'text-red-300' : 'text-slate-400',
                  ].join(' ')}
                >
                  {idCollision
                    ? ui.adminFieldEnNameCollision.replace('{id}', derivedId)
                    : derivedId
                      ? ui.adminFieldEnNameHint.replace('{id}', derivedId)
                      : ui.adminFieldEnNameHintEmpty}
                </p>
              )}
            </div>
          );
        })}

        {/* URL per brand × lang — shares the lang tab above, so switching the tab
            shows this lang's two brand URLs. Fallback logic (pickBrandLang) lets
            you leave a slot empty; the runtime will show brand.en then google.en. */}
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {ALL_BRANDS.map((b) => (
            <div key={b}>
              <label className="block text-xs text-slate-500">
                {ui.adminFieldUrl} — {BRAND_LABEL[b]} · {LANG_LABEL[editingLang]}
              </label>
              <input
                type="url"
                value={draft.url[b][editingLang]}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    url: {
                      ...draft.url,
                      [b]: { ...draft.url[b], [editingLang]: e.target.value },
                    },
                  })
                }
                placeholder="https://…"
                className="mt-1 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-[var(--accent-muted)]"
              />
            </div>
          ))}
        </div>

        {/* Participating toggle */}
        <label className="mt-5 flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={draft.isParticipating}
            onChange={(e) => setDraft({ ...draft, isParticipating: e.target.checked })}
            className="accent-[var(--accent)]"
          />
          <span className="text-sm">{ui.adminFieldParticipating}</span>
        </label>

        <ErrorBanner error={upsert.error} lang={lang} onDismiss={() => upsert.reset()} />

        <div className="mt-6 flex items-center justify-end gap-3">
          {/* Save-blockers surface here for the case where the editor never
              opened the EN tab — the inline hint under EN name wouldn't be
              visible, so we mirror it next to the save button. */}
          {enNameMissing && (
            <p className="text-[11px] text-amber-300/80 leading-relaxed">
              {ui.adminFieldEnNameRequired}
            </p>
          )}
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-sm hover:bg-slate-100"
          >
            {ui.adminCancel}
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={enNameMissing || idCollision || upsert.isPending}
            className="inline-flex items-center gap-2 rounded-full accent-bg text-white px-4 py-2 text-sm font-medium disabled:opacity-40 hover:brightness-110"
          >
            {upsert.isPending && <Loader2 size={16} className="animate-spin" />}
            {ui.adminSave}
          </button>
        </div>
      </div>
    </div>
  );
}
